import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { getThousandComma } from "../../../../Api/ApiCommon";
import { useApi } from "../../../../hooks/useApi";
import useIsMobile from "../../../../hooks/useIsMobile";
import Flex from "../../../DefaultStyles/Flex";
import LineBox from "../../../DefaultStyles/LineBox";
import DropDown, { OptionType } from "../../../Filters/DropDown";
import DashBoardToggleBtn from "../DashBoardToggleBtn";
import { ISeries } from "../PayBoard/PayHistorySection";
import {
  AxixTitle,
  ChartContainer,
  DashBoardSectionTitle,
  DashBoardSectionTitleContainer,
  DashBoardViewDate,
  IndateSectionProps,
} from "../UserBoard/IndateSection";

interface PointUsingSectionProps extends IndateSectionProps {}

export default function PointUsingSection({
  filter,
  setFilter,
}: PointUsingSectionProps) {
  const isMobile = useIsMobile();

  const [detailList, setDetailList] = useState("");
  const [typeValue, setTypeValue] = useState<OptionType[]>([]);
  const [series, setSeries] = useState<ISeries[]>([]);
  const [date, setDate] = useState<string[]>([]);

  const optionList = [
    { value: "101", label: "포인트 적립" },
    { value: "104", label: "포인트 환불" },
    { value: "201", label: "포인트 사용(결제)" },
    { value: "202", label: "링키드 머니로 전환" },
    { value: "203", label: "포인트 만료" },
  ];

  const { response } = useApi({
    method: "get",
    url:
      filter.startDate !== "" && filter.endDate !== ""
        ? `dash/total?type=point&subtype=type&startdate=${dayjs(
            filter.startDate
          )
            .add(1, "day")
            ?.format("YYYY-MM-DD")}&enddate=${dayjs(filter.endDate)
            .add(1, "day")
            ?.format("YYYY-MM-DD")}&filter=day` +
          (detailList !== "" ? `&detail=${detailList}` : ``)
        : undefined,
  });

  useEffect(() => {
    let series: any[] = [];
    let date: string[] = [];

    if (response?.status === 200) {
      const objByDetail = response.data.reduce((group: any, product: any) => {
        const { detail } = product;

        group[detail] = group[detail] ?? [];
        group[detail].push(product);

        return group;
      }, {});

      const arrByDetail = Object.entries(objByDetail);

      const filteredArr = arrByDetail.filter(
        (element) => element[0] !== "102" && element[0] !== "103"
      );

      for (let i = 0; i < filteredArr.length; i++) {
        const element = filteredArr[i] as any[];
        const elementData = element[1];

        let list = [];

        for (let j = 0; j < elementData.length; j++) {
          const el = elementData[j];

          list.push(filter?.isAccum ? el.data.total ?? 0 : el.data.count ?? 0);
        }

        series.push({
          data: list.reverse(),
          name:
            element[0] === "101"
              ? "포인트 적립"
              : element[0] === "104"
              ? "포인트 환불"
              : element[0] === "201"
              ? "포인트 사용"
              : element[0] === "202"
              ? "링키드 머니로 전환"
              : element[0] === "203"
              ? "포인트 만료"
              : "",
        });
      }

      for (let i = 0; i < response?.data.length; i++) {
        const element = response?.data[i];

        date.push(dayjs(element.date).add(-1, "day").format("YYYY-MM-DD"));
      }

      const set = new Set(date.reverse());
      const uniqueDate = [...set];

      setDate(uniqueDate);
      setSeries(series);
    }
  }, [response, filter]);

  useEffect(() => {
    const arr: string[] = [];
    const keyArr: string[] = [];

    for (let i = 0; i < typeValue.length; i++) {
      const element = typeValue[i];

      arr.push(`"${element?.value}"`);
      keyArr.push(`${element?.value}`);
    }

    if (keyArr?.includes("전체")) {
      setDetailList("");
    } else {
      setDetailList(arr.toString());
    }
  }, [typeValue]);

  useEffect(() => {}, [series]);

  return (
    <>
      <DashBoardSectionTitleContainer>
        <DashBoardSectionTitle>포인트 이용 현황</DashBoardSectionTitle>

        <Flex gapSize={10}>
          <DropDown
            isDisabled={false}
            initText="전체"
            setDropDownMultiValue={setTypeValue}
            optionList={optionList}
            dropdownMultiValue={typeValue}
            isMulti={true}
            boxWidth={220}
          />

          <DashBoardToggleBtn
            btnTextArray={["일별", "누적"]}
            filter={filter}
            setFilter={setFilter}
          />
        </Flex>
      </DashBoardSectionTitleContainer>

      <LineBox boxPadding={"28px 32px"}>
        <DashBoardViewDate isMobile={isMobile}>
          조회 기간 : {filter.startDate} ~ {filter.endDate}
        </DashBoardViewDate>

        <ChartContainer>
          <AxixTitle>원</AxixTitle>

          <ReactApexChart
            type="bar"
            height={300}
            series={series}
            options={{
              chart: {
                type: "bar",

                toolbar: {
                  show: false,
                },
                zoom: {
                  enabled: false,
                },
                events: {
                  mounted(chart) {
                    chart.windowResizeHandler();
                  },
                },
              },
              responsive: [
                {
                  breakpoint: 480,
                  options: {
                    legend: {
                      position: "bottom",
                      offsetX: -10,
                      offsetY: 0,
                    },
                  },
                },
              ],
              plotOptions: {
                bar: {
                  horizontal: false,
                  borderRadius: series?.length === 3 ? 10 : 0,
                  dataLabels: {
                    position: "top",
                  },
                },
              },
              dataLabels: {
                enabled: true,
                formatter: (value) => {
                  if (Number(value) > 0) {
                    return getThousandComma(value);
                  } else {
                    return "";
                  }
                },
                distributed: true,
                offsetY: -20,
                style: {
                  fontSize: "11px",
                  colors: ["#304758"],
                },
              },
              xaxis: {
                labels: {
                  formatter: (value) => {
                    return dayjs(value).format("M/DD (dd)");
                  },
                },
                categories: date,
                position: "left",
              },
              yaxis: {
                labels: {
                  formatter: (value) => {
                    return getThousandComma(value ?? 0);
                  },
                },
              },
              legend: {
                position: "bottom",
                offsetY: 7,
                fontFamily:
                  "-apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', Pretendard Variable, Roboto, 'Noto Sans KR', 'Segoe UI', 'Malgun Gothic', sans-serif",
                markers: {
                  width: 10,
                  height: 10,
                  offsetX: -4,
                  radius: 10,
                },
                fontSize: "13px",
                fontWeight: 500,
                itemMargin: {
                  horizontal: 16,
                },
              },
              fill: {
                opacity: 1,
              },
              stroke: {
                show: true,
                width: 2,
                colors: ["transparent"],
              },
              tooltip: {
                x: {
                  formatter: (value) => {
                    return dayjs(value).format("M/DD (dd)");
                  },
                },
                y: {
                  formatter: (value) => getThousandComma(value ?? 0) + "원",
                },
              },
            }}
          />

          <AxixTitle isX>날짜</AxixTitle>
        </ChartContainer>
      </LineBox>
    </>
  );
}
